/**
 * Room tone for the index: wind over open ground, and the low sustain of a large
 * stone space.
 *
 * Synthesised rather than sampled, for three reasons that all point the same way.
 * A recording good enough to loop seamlessly costs more bytes than the whole
 * JavaScript bundle, and this page already carries five exhibitions of geometry.
 * A field recording carries a licence, and every licence here has to be named in
 * the credits panel and travel with the work. And a loop of any length eventually
 * announces its own seam, which is exactly the moment the room stops feeling like
 * a room. Filtered noise has no seam because it never repeats.
 *
 * What this cannot do is anything with a throat: a falcon, a camel, a call to
 * prayer. Those are recordings or they are nothing, and they would need sourcing
 * and clearing rather than code.
 *
 * Silent until asked. Sound that starts on its own is the single most reliably
 * resented thing a site can do, and browsers block it anyway — an AudioContext
 * created without a gesture starts suspended.
 */

import type { UiCopy } from "./i18n";

export type Ambience = { element: HTMLElement; dispose: () => void };

const FADE_SECONDS = 2.5;
// Anything that counts as a user gesture for the autoplay policy.
const GESTURES = ["pointerdown", "keydown", "touchstart", "wheel"] as const;
const STORAGE_KEY = "simam-heritage-ambience";

type Engine = { context: AudioContext; master: GainNode; stop: () => void };

/** Eight seconds of noise, looped. Long enough that the ear cannot find the join. */
function noiseBuffer(context: AudioContext): AudioBuffer {
  const length = context.sampleRate * 8;
  const buffer = context.createBuffer(1, length, context.sampleRate);
  const channel = buffer.getChannelData(0);
  // Brown-ish noise: integrating white noise tilts the spectrum down, which is what
  // separates wind from static. Plain white noise reads as a broken speaker.
  let last = 0;
  for (let i = 0; i < length; i += 1) {
    const white = Math.random() * 2 - 1;
    last = (last + 0.02 * white) / 1.02;
    channel[i] = last * 3.5;
  }
  return buffer;
}

function build(context: AudioContext): Engine {
  const master = context.createGain();
  master.gain.value = 0;
  master.connect(context.destination);

  const wind = context.createBufferSource();
  wind.buffer = noiseBuffer(context);
  wind.loop = true;

  // A bandpass sweeping slowly is what makes noise read as moving air rather than
  // a hiss sitting still behind the page.
  const band = context.createBiquadFilter();
  band.type = "bandpass";
  band.frequency.value = 480;
  band.Q.value = 0.7;

  const gust = context.createOscillator();
  gust.frequency.value = 0.05;
  const gustDepth = context.createGain();
  gustDepth.gain.value = 260;
  gust.connect(gustDepth).connect(band.frequency);

  const windLevel = context.createGain();
  windLevel.gain.value = 0.16;

  const breath = context.createOscillator();
  breath.frequency.value = 0.07;
  const breathDepth = context.createGain();
  breathDepth.gain.value = 0.07;
  breath.connect(breathDepth).connect(windLevel.gain);

  wind.connect(band).connect(windLevel).connect(master);

  // The drone. Two oscillators a few cents apart beat against each other slowly,
  // which is the acoustic signature of a big hard-walled room.
  const drone = context.createGain();
  drone.gain.value = 0.05;
  const roll = context.createBiquadFilter();
  roll.type = "lowpass";
  roll.frequency.value = 220;
  roll.connect(drone).connect(master);

  const voices = [55, 82.5].map((frequency, index) => {
    const oscillator = context.createOscillator();
    oscillator.type = "sine";
    oscillator.frequency.value = frequency;
    oscillator.detune.value = index === 0 ? -6 : 5;
    oscillator.connect(roll);
    oscillator.start();
    return oscillator;
  });

  wind.start();
  gust.start();
  breath.start();

  return {
    context,
    master,
    stop: () => {
      for (const node of [wind, gust, breath, ...voices]) node.stop();
    },
  };
}

export function createAmbience(copy: UiCopy): Ambience {
  let engine: Engine | null = null;
  // Armed by default. A browser will not let this make a sound before the visitor has
  // interacted with the page, so "on" here means the room tone is waiting for the first
  // gesture rather than already playing — which is why the control reads its state from
  // the preference and not from whether the context is currently running.
  let on = ambiencePreference();
  let armed: (() => void) | null = null;

  const button = document.createElement("button");
  button.type = "button";
  button.className = "ambience";
  button.setAttribute("aria-pressed", "false");
  button.title = copy.ambienceHint;

  const icon = document.createElement("span");
  icon.className = "ambience__icon";
  icon.setAttribute("aria-hidden", "true");
  for (let i = 0; i < 4; i += 1) {
    const bar = document.createElement("i");
    bar.style.setProperty("--bar", String(i));
    icon.append(bar);
  }

  const label = document.createElement("span");
  label.className = "ambience__label";

  const paint = (): void => {
    button.setAttribute("aria-pressed", on ? "true" : "false");
    button.dataset.on = on ? "true" : "false";
    label.textContent = on ? copy.ambienceOn : copy.ambienceOff;
    // The pressed state is carried by aria-pressed, so the accessible name has to
    // stay the same either way or a screen reader announces a rename each toggle.
    button.setAttribute("aria-label", copy.ambienceLabel);
  };

  const ramp = (target: number): void => {
    if (engine === null) return;
    const { context, master } = engine;
    const now = context.currentTime;
    master.gain.cancelScheduledValues(now);
    master.gain.setValueAtTime(master.gain.value, now);
    master.gain.linearRampToValueAtTime(target, now + FADE_SECONDS);
  };

  const enable = async (): Promise<void> => {
    if (engine === null) {
      // Older Safari still only exposes the prefixed constructor, and a kiosk build
      // pinned to an old WebView is exactly where this page is expected to run.
      // The DOM lib declares AudioContext as always present, which is the one thing
      // that is not true on the browsers this fallback exists for.
      const scope = window as unknown as {
        AudioContext?: typeof AudioContext;
        webkitAudioContext?: typeof AudioContext;
      };
      const Ctor = scope.AudioContext ?? scope.webkitAudioContext;
      if (Ctor === undefined) return;
      engine = build(new Ctor());
    }
    await engine.context.resume();
    if (engine.context.state === "running") {
      ramp(0.9);
      return;
    }
    // Autoplay policy: a context created without a gesture starts suspended and resume()
    // is refused. Wait for the first real interaction and start then.
    if (armed !== null) return;
    const start = (): void => {
      disarm();
      if (!on) return;
      void engine?.context.resume().then(() => ramp(0.9));
    };
    const disarm = (): void => {
      for (const name of GESTURES) window.removeEventListener(name, start);
      armed = null;
    };
    armed = disarm;
    for (const name of GESTURES) window.addEventListener(name, start, { once: false, passive: true });
  };

  const disable = (): void => {
    armed?.();
    ramp(0);
  };

  const toggle = (): void => {
    on = !on;
    paint();
    try {
      window.localStorage.setItem(STORAGE_KEY, on ? "on" : "off");
    } catch {
      // Private-mode kiosks throw on write. The toggle still works for this visit.
    }
    if (on) void enable();
    else disable();
  };

  button.addEventListener("click", toggle);
  button.append(icon, label);
  paint();
  // Start arming immediately when the preference says on, so the first click anywhere
  // brings the room up rather than the visitor having to find this control.
  if (on) void enable();

  return {
    element: button,
    dispose: () => {
      armed?.();
      button.removeEventListener("click", toggle);
      if (engine === null) return;
      engine.stop();
      void engine.context.close();
      engine = null;
    },
  };
}

/**
 * Whether the room tone should be on: an explicit choice if there is one, else yes.
 *
 * Defaulting to on does not make the page play sound at load — the browser forbids that
 * — it means the tone is waiting for the visitor's first gesture. Anyone who turns it
 * off is remembered.
 */
export function ambiencePreference(): boolean {
  try {
    const value = window.localStorage.getItem(STORAGE_KEY);
    if (value === "off") return false;
    return true;
  } catch {
    return true;
  }
}
